import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell, CheckCircle2, Clock, X, RotateCcw, Search, ExternalLink,
  AlertTriangle, Calendar, Loader2, Plus, Filter,
} from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";

type ReminderItem = {
  id: string;
  titre: string;
  description: string | null;
  sourceType: string;
  sourceId: string | null;
  rappelDate: string;
  statut: string;
  lienUrl: string | null;
  priorite: string;
  assignePrenom: string | null;
  assigneNom: string | null;
  traiteAt: string | null;
  isTaskReminder?: boolean;
};

const PRIORITE_CONFIG: Record<string, { label: string; color: string }> = {
  basse:   { label: "Basse",   color: "bg-slate-100 text-slate-700 border-slate-200" },
  moyenne: { label: "Moyenne", color: "bg-blue-100 text-blue-700 border-blue-200" },
  haute:   { label: "Haute",   color: "bg-orange-100 text-orange-700 border-orange-200" },
};

const SOURCE_LABELS: Record<string, string> = {
  client: "Client", mission: "Mission", ticket: "Ticket",
  action: "Action", manuel: "Manuel", prospect: "Prospect",
  revendeur: "Revendeur", commercial: "Commercial",
};

const TABS = [
  { key: "actif",   label: "À traiter",  icon: AlertTriangle, color: "text-red-500" },
  { key: "a_venir", label: "À venir",    icon: Calendar,       color: "text-blue-500" },
  { key: "traite",  label: "Traités",    icon: CheckCircle2,   color: "text-green-500" },
] as const;

export default function RappelsList() {
  const [tab, setTab] = useState<"actif" | "a_venir" | "traite">("actif");
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [_, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: items = [], isLoading } = useQuery<ReminderItem[]>({
    queryKey: ["/api/reminders", tab, search],
    queryFn: async () => {
      const params = new URLSearchParams({ statut: tab, search });
      const res = await authFetch(`/api/reminders?${params}`);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    refetchInterval: 60_000,
  });

  const mutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      return (await authFetch(`/api/reminders/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      })).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reminders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/reminders/count"] });
      toast({ title: "Rappel mis à jour" });
    },
    onError: () => toast({ variant: "destructive", title: "Erreur" }),
  });

  const handleTraite = (id: string) => mutation.mutate({ id, updates: { statut: "traite" } });
  const handleIgnore = (id: string) => mutation.mutate({ id, updates: { statut: "ignore" } });
  const handleReporter = (id: string, date: string) => mutation.mutate({ id, updates: { statut: "reporte", rappelDate: date } });

  const filteredItems = sourceFilter
    ? items.filter(i => i.sourceType === sourceFilter)
    : items;

  const allSources = [...new Set(items.map(i => i.sourceType))].filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Centre de rappels</h1>
          <p className="text-muted-foreground mt-1">Gérez tous vos rappels et relances</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-0">
        {TABS.map(({ key, label, icon: Icon, color }) => {
          const count = tab === key ? filteredItems.length : undefined;
          return (
            <button
              key={key}
              onClick={() => { setTab(key); setSourceFilter(""); }}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                tab === key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              data-testid={`tab-rappels-${key}`}
            >
              <Icon className={cn("h-4 w-4", tab === key ? color : "")} />
              {label}
              {tab === key && count !== undefined && (
                <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">
                  {count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Rechercher un rappel..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="input-rappels-search"
          />
        </div>
        {allSources.length > 1 && (
          <div className="flex items-center gap-1.5">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <div className="flex gap-1">
              <button
                onClick={() => setSourceFilter("")}
                className={cn(
                  "text-xs px-2.5 py-1.5 rounded-full border transition-colors",
                  !sourceFilter ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:bg-secondary"
                )}
              >
                Tous
              </button>
              {allSources.map(src => (
                <button
                  key={src}
                  onClick={() => setSourceFilter(sourceFilter === src ? "" : src)}
                  className={cn(
                    "text-xs px-2.5 py-1.5 rounded-full border transition-colors capitalize",
                    sourceFilter === src ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:bg-secondary"
                  )}
                >
                  {SOURCE_LABELS[src] || src}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Bell className="h-14 w-14 text-muted-foreground/20 mb-4" />
          <p className="text-lg font-medium text-muted-foreground">
            {tab === "actif" ? "Aucun rappel à traiter" :
             tab === "a_venir" ? "Aucun rappel prévu dans les 7 prochains jours" :
             "Aucun rappel traité récemment"}
          </p>
          <p className="text-sm text-muted-foreground/60 mt-1">
            Les rappels apparaissent automatiquement ou peuvent être créés manuellement.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filteredItems.map(item => {
            const prioriteConf = PRIORITE_CONFIG[item.priorite] || PRIORITE_CONFIG.moyenne;
            const rappelDate = new Date(item.rappelDate);
            const isOverdue = rappelDate < new Date() && item.statut === 'actif';

            return (
              <div
                key={item.id}
                className={cn(
                  "border rounded-xl p-4 space-y-3 transition-all hover:shadow-md",
                  item.isTaskReminder
                    ? "border-amber-200 bg-amber-50/50 dark:border-amber-800/40 dark:bg-amber-950/20"
                    : isOverdue
                    ? "border-red-200 bg-red-50/30 dark:border-red-800/30 dark:bg-red-950/10"
                    : "border-border bg-card"
                )}
                data-testid={`reminder-row-${item.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {item.isTaskReminder && (
                        <Badge variant="outline" className="text-[10px] px-1.5 border-amber-400 text-amber-700 bg-amber-50">
                          Action relancée
                        </Badge>
                      )}
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 border", prioriteConf.color)}>
                        {prioriteConf.label}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 text-muted-foreground capitalize">
                        {SOURCE_LABELS[item.sourceType] || item.sourceType}
                      </Badge>
                    </div>
                    <p className="font-medium text-sm leading-snug" data-testid={`text-rappel-titre-${item.id}`}>
                      {item.titre}
                    </p>
                    {item.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
                    )}
                  </div>
                  {item.lienUrl && (
                    <button
                      onClick={() => navigate(item.lienUrl!)}
                      className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className={cn("flex items-center gap-1", isOverdue && "text-red-600 font-medium")}>
                    <Clock className="h-3 w-3" />
                    {isOverdue
                      ? `En retard · ${formatDistanceToNow(rappelDate, { locale: fr })}`
                      : format(rappelDate, "dd/MM/yyyy à HH:mm", { locale: fr })
                    }
                  </span>
                  {(item.assignePrenom || item.assigneNom) && (
                    <span>{item.assignePrenom} {item.assigneNom}</span>
                  )}
                </div>

                {item.statut === 'actif' && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-border/50">
                    <Button
                      size="sm"
                      className="h-7 text-xs px-2.5 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleTraite(item.id)}
                      disabled={mutation.isPending}
                      data-testid={`btn-traite-${item.id}`}
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Traité
                    </Button>
                    <ReporterPopover
                      onReporter={(date) => handleReporter(item.id, date)}
                      isPending={mutation.isPending}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs px-2 text-muted-foreground hover:text-destructive"
                      onClick={() => handleIgnore(item.id)}
                      disabled={mutation.isPending}
                      data-testid={`btn-ignorer-${item.id}`}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Ignorer
                    </Button>
                  </div>
                )}

                {item.statut !== 'actif' && (
                  <div className="text-xs pt-1 border-t border-border/50">
                    {item.statut === 'traite' && item.traiteAt && (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <CheckCircle2 className="h-3 w-3" />
                        Traité le {format(new Date(item.traiteAt), "dd/MM/yyyy", { locale: fr })}
                      </span>
                    )}
                    {item.statut === 'ignore' && <span className="text-muted-foreground">Ignoré</span>}
                    {item.statut === 'reporte' && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <RotateCcw className="h-3 w-3" />
                        Reporté au {format(rappelDate, "dd/MM/yyyy", { locale: fr })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReporterPopover({ onReporter, isPending }: { onReporter: (date: string) => void; isPending: boolean }) {
  const [open, setOpen] = useState(false);
  const [customDate, setCustomDate] = useState("");

  const quick = [
    { label: "Dans 3 jours", days: 3 },
    { label: "Dans 1 semaine", days: 7 },
    { label: "Dans 2 semaines", days: 14 },
    { label: "Dans 1 mois", days: 30 },
    { label: "Dans 3 mois", days: 90 },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs px-2.5" disabled={isPending} data-testid="btn-reporter-popover">
          <RotateCcw className="h-3 w-3 mr-1" />
          Reporter
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" side="top">
        <p className="text-xs font-medium px-2 py-1 text-muted-foreground">Reporter au...</p>
        {quick.map(({ label, days }) => (
          <button
            key={days}
            className="w-full text-xs text-left px-2 py-1.5 rounded hover:bg-secondary transition-colors"
            onClick={() => {
              onReporter(new Date(Date.now() + days * 24 * 3600 * 1000).toISOString());
              setOpen(false);
            }}
          >
            {label}
          </button>
        ))}
        <div className="border-t mt-1 pt-1 flex gap-1">
          <Input
            type="datetime-local"
            className="h-7 text-xs flex-1"
            value={customDate}
            onChange={e => setCustomDate(e.target.value)}
          />
          <Button
            size="sm"
            className="h-7 text-xs px-2 shrink-0"
            disabled={!customDate}
            onClick={() => {
              if (customDate) {
                onReporter(new Date(customDate).toISOString());
                setOpen(false);
                setCustomDate("");
              }
            }}
          >
            OK
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
